
import wait from 'waait'
import isNull from 'lodash/isNull'
import { differenceInDays } from 'date-fns'
import transformParams from '@/helpers/transformParams'
import apiDebugger from '@/helpers/apiDebugger'
import analytics from '@/helpers/analytics'
import { hotels as pairings } from '@/static/data/pairings.json'
import insertSojernTag from '@/helpers/insertSojernTag'

let activeSearchUuid = null

export default {
  data () {
    return {
      apiData: null,
      uuid: null,
      nextPollUrl: null,
      nextPollSleep: 1000,
      pollCount: 0,
      pollLimit: 180,
      cooldown: null,
      isLoading: true,
      inlineAds: null,
      hasShareTripsChat: false,
      debugData: null,
      filtersOpen: false,
      viewMode: 'list'
    }
  },
  computed: {
    rtl () {
      return this.$store.state.rtl
    },
    country () {
      return this.$store.state.country
    },
    isMobile () {
      return this.$store.state.device.isMobile
    },
    hasSearchResults () {
      return this.$store.state.searchResults.length > 0
    },
    activeFilterCount () {
      const checkboxes = this.$store.state.searchHotelsCheckboxes
      if (!checkboxes || typeof checkboxes !== 'object') return 0
      let unchecked = 0
      Object.values(checkboxes).forEach(group => {
        if (group && group.options) {
          Object.values(group.options).forEach(opt => {
            if (opt && !opt.checked) unchecked++
          })
        }
      })
      return unchecked
    }
  },
  watch: {
    '$route.query': {
      handler: 'load',
      deep: true
    },
    viewMode (mode) {
      if (!process.client) return
      if (this.isMobile && mode === 'map') {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  beforeDestroy () {
    if (process.client) {
      document.body.style.overflow = ''
    }
  },
  mounted () {
    this.load()
    try {
      window.adara('send', {
        pageType: 'Results Page',
        siteLanguage: this.$store.state.language,
        siteCountry: this.$store.state.country
      })
    } catch (e) {
      console.info('Error firing Adara event')
    }
  },
  methods: {
    async load () {
      const { query } = this.$route
      const { country, site, entryId, siteType, isIdentified } = this.$store.state
      const { deviceCategory } = this.$store.getters

      this.isLoading = true

      setTimeout(() => {
        this.scrollTo('hotel-results')
      }, 500)

      if (!isIdentified) {
        console.info('Wait for visitor identification...')
        return setTimeout(() => {
          this.load()
        }, 500)
      }

      const now = new Date()

      if (isNull(this.cooldown) || (now > this.cooldown)) {
        this.cooldown = new Date()
        this.cooldown.setSeconds(this.cooldown.getSeconds() + 5)
      } else {
        console.info('Waiting for cooldown...')
        return setTimeout(() => {
          this.load()
        }, 1000)
      }

      this.uuid = crypto.randomUUID()
      activeSearchUuid = this.uuid

      this.$store.commit('clearSearchHotelsFilters')
      this.$store.commit('resetSearchResults')
      
      // if (!query.pickupCode || !query.dropoffCode || !query.pickupDate || !query.dropoffDate) {
      //   return this.$router.push('/')
      // }

      const apiData = transformParams.toApi(query, pairings)
      this.apiData = apiData

      let stayNights = 1
      if (apiData.startDate && apiData.endDate) {
        stayNights = Math.max(
          1,
          differenceInDays(new Date(apiData.endDate), new Date(apiData.startDate))
        )
      }
      this.$store.commit('setHotelStayNights', stayNights)

      // Sojern
      try {
        const sojernParams = {
          p: 'search',
          eml: this.$store.state.user.data.email,
          ccid: this.$store.state.entryId,
          tad: null,
          tch: null,
          t: null,
          ppot: 'leisure',
          l: window.navigator.language,
          vid: 'hotel',
          fow: null,
          hc: null,
          cd: differenceInDays(new Date(apiData.inboundDate), new Date(apiData.outboundDate)),
          pname: window.location.href,
          rc1: apiData.originText.match(/-\s*([\w\s]+),/)?.[1]?.trim() ?? null,
          rs2: apiData.originText.match(/,\s*([A-Z]{2})\s*,/)?.[1]?.trim() ?? null,
          rc2: apiData.destinationText.match(/-\s*([\w\s]+),/)?.[1]?.trim() ?? null,
          rs1: apiData.destinationText.match(/,\s*([A-Z]{2})\s*,/)?.[1]?.trim() ?? null,
          rd1: apiData.outboundDate,
          rd2: apiData.inboundDate,
          ra1: apiData.origin,
          rn1: null,
          rn2: null
        }

        insertSojernTag(sojernParams, this)
      } catch (e) {
        console.info('Error when inserting the Sojern script')
      }

      const params = {
        ...apiData,
        ...(this.$store.state.visitorPayload || {}),
        debug: this.$store.state.isDebugEnabled ? 1 : 0,
        device: deviceCategory,
        country: country || 'WW',
        site: site.replace('v2.', '').replace('new.', '').replace('ab.', '').replace('dev-ab.', ''),
        entryId,
        adid: query.gclid || query.msclkid,
        sitebrand: siteType
      }

      // Results
      try {
        const {
          deeplinks,
          results,
          search,
          filters,
          debug
        } = await this.$axios.$get(this.$config.API_URL + '/hotels/search', { params })
        if (results) {
          this.$store.commit('appendSearchResults', Object.values(results))
        }

        if (search && search.totalCount == 0 && search.finished == true) {
          const debuggerPayload = {
            params: params,
            search: search,
          }
          apiDebugger.log('no-hotel-results', entryId, debuggerPayload, this)

          analytics.track('meta-no-hotel-results-response', this)
        }

        if (debug !== undefined && debug.debugUrl !== undefined) {
          this.debugData = debug
        }

        // if (params?.origin && params?.destination) {
        //   this.$store.commit('setSearchHotelLocationCodes', { pickupCode: params.origin, dropoffCode: params.destination })
        // }

        if (deeplinks) {
          this.$store.commit('setDeeplinks', Object.values(deeplinks))
        }

        if (filters) {
          this.$store.commit('setSearchHotelsFilters', filters)
          this.$store.commit('setSearchHotelsCheckboxes', filters)
        }

        if (search && !search.finished) {
          this.nextPollUrl = search.nextPollUrl
          this.nextPollSleep = search.nextPollSleep
          this.pollCount = 0
          this.poll()
        }

        if (search.finished) this.isLoading = false
      } catch (e) {
        console.info('Error getting hotel results')
        return this.$toast.error('An error has ocurred, please reload and try again', { position: 'top-center', duration: 4000 })
      }

      // Kayak
      try {
        params.sitebrand = 'hotels'
        // params.site= 'multiseater.com'
        const { inlineItems } = await this.$axios.$get(this.$config.API_URL + '/ads/inline/hotels', { params })

        this.$store.commit('setInlineAds', inlineItems)

        if (inlineItems !== undefined) {
        /*
          // Disabled for CORS error
          inlineItems.forEach(item => {
            try {
              this.$axios.get(item.impressionUrl, null, {
                "Origin": "farescraper.com",
                "User-Agent": ostype
              });
            } catch (ex) {
              console.info('Error triggering impressionUrl')
            }
          })
        */
        }

      } catch (e) {
        console.info('Error inserting Kayak ads')
      }

      //
    },
    async poll (searchUuid) {
      if (!searchUuid) searchUuid = this.uuid

      if (this.pollCount >= this.pollLimit) {
        console.info('Poll limit reached')
        return
      }

      this.pollCount = this.pollCount + 1
      await wait(this.nextPollSleep)

      if (searchUuid !== activeSearchUuid) {
        console.info('Poll aborted: new search started')
        return
      }

      try {
        const { results, search, filters } = await this.$axios.$get(this.nextPollUrl)

        if (searchUuid !== activeSearchUuid) return

        if (results) {
          this.$store.commit('mergeSearchResults', Object.values(results))

          if (!this.$store.state.hasLocalCurrency) {
            const firstResult = results[Object.keys(results)[0]]
            if (firstResult && firstResult.currency) {
              const localFlag = String(firstResult.currency).toUpperCase() !== 'USD'
              this.$store.commit('setHasLocalCurrency', localFlag)
              this.$store.commit('setLocalCurrency', localFlag)
            }
          }
        }

        if (filters) {
          const current = this.$store.state.searchHotelsCheckboxes
          const currentKeys = current ? Object.keys(current).sort().join(',') : ''
          const newKeys = Object.keys(filters).sort().join(',')
          if (currentKeys !== newKeys) {
            this.$store.commit('setSearchHotelsCheckboxes', filters)
          }
        }

        if (search && !search.finished) {
          this.nextPollUrl = search.nextPollUrl
          this.nextPollSleep = search.nextPollSleep
          this.poll(searchUuid)
        }

        if (search.finished) this.isLoading = false
      } catch (e) {
        console.info('Error polling')
      }
    },
    scrollTo (id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
  }
}
